/* eslint-disable react/prop-types */
/*
Author: Chaitanya
*/

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createStackNavigator,
  CardStyleInterpolators,
} from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isWeb } from '@global/screens';
import Toast from 'react-native-toast-message';

import {
  setTitleHeader,
  setDefaultStackOption,
  hasNoAccess,
} from '@global/helpers';

import HamburgerButton from '@components/navbar/HamburgerButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import BackButton from '@components/navbar/BackButton';
import MoreButton from '@components/navbar/MoreButton';

import SkpInternalScreen from '@/screens/skp/skp-internal/SkpInternalScreen';
import SkpInternalDetailScreen from '@/screens/skp/skp-internal/SkpInternalDetailScreen';
import SkpInternalFormScreen from '@/screens/skp/skp-internal/SkpInternalFormScreen';
import SkpInternlBudgetTableScreen from '@/screens/skp/skp-internal/SkpInternlBudgetTableScreen';
import SkpExternalListScreen from '@/screens/skp/skp-external/SkpExternalListScreen';

import { MsOrder } from '@api/ms-order';
import { showAlert } from '@/store/global';
import { showPopup } from '@/store/modal';

const Stack = createStackNavigator();

const SkpInternalNavigator = ({ navigation }) => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const tempData = useSelector((state) => state.global.tempData);
  const skpGenerateStatus = useSelector(
    (state) => state.global.skpGenerateStatus,
  );

  return (
    <Stack.Navigator
      initialRouteName="SkpInternalScreen"
      screenOptions={(props) => ({
        headerLeft: () => <HamburgerButton {...props} />,
        headerRight: () => <NotificationSettingButton {...props} />,
        ...setDefaultStackOption(insets, CardStyleInterpolators, true),
      })}>
      <Stack.Screen
        name="SkpInternalScreen"
        component={SkpInternalScreen}
        options={(props) => {
          let isBack = false;
          let title = 'SKP Internal';
          if (props?.route?.params?.titleScreen) {
            isBack = true;
            title = `${title} ${
              !props?.route?.params?.isHome
                ? props.route.params.titleScreen
                : ''
            }`;
          }
          const hasSkpExportAccess = !hasNoAccess([
            'superadmin',
            'general_manager_marketing',
            'vice_president',
            'general_manager',
            'distributor_manager',
            'regional_manager',
            'regional_support',
            'key_account_manager',
            'group_key_account_manager',
            'national_key_account_manager',
            'branch_head',
            'sales_admin',
            'sales_supervisor',
            'salesman',
            'finance_supervisor',
            'vice_president_finance',
            'budget_controller',
            'finance_head',
            'tax_support',
            'marketing_support',
            'marketing_admin',
            'billing_supervisor',
            'chief_of_staff',
            'general',
            'regional_distributor_manager',
          ]);
          const showFilter = () => {
            dispatch(showPopup({ name: 'showFilter', value: 'skp-internal' }));
          };
          const showExport = () => {
            dispatch(showPopup({ name: 'showSkpExport', value: true }));
          };
          const goBack = () => {
            props.navigation.navigate('HomeNavigator');
          };
          const menus = [
            { type: 'function', name: 'Filter', callback: showFilter },
          ];
          if (hasSkpExportAccess) {
            menus.unshift({
              type: 'function',
              name: 'Export',
              callback: showExport,
            });
          }
          return {
            title: setTitleHeader(title),
            headerLeft: () =>
              isBack ? (
                <BackButton {...props} onPress={() => goBack()} />
              ) : (
                <HamburgerButton {...props} />
              ),
            headerRight: () =>
              isWeb ? (
                <NotificationSettingButton {...props} />
              ) : (
                <MoreButton menu={menus} {...props} />
              ),
          };
        }}
      />
      <Stack.Screen
        name="SkpInternalFormScreen"
        component={SkpInternalFormScreen}
        options={(props) => {
          const route = props.route;
          let title = 'Add SKP Internal';
          if (route.params && route.params.titleScreen) {
            title = 'Edit SKP Internal';
          }
          const goBack = () => {
            if (route.params && route.params.titleScreen) {
              props.navigation.navigate(
                'SkpInternalDetailScreen',
                route.params,
              );
            } else {
              props.navigation.navigate('HomeNavigator');
            }
          };
          return {
            title: setTitleHeader(title),
            headerLeft: () => (
              <BackButton {...props} onPress={() => goBack()} />
            ),
            headerRight: isWeb
              ? () => <NotificationSettingButton {...props} />
              : false,
          };
        }}
      />
      <Stack.Screen
        name="SkpInternalDetailScreen"
        component={SkpInternalDetailScreen}
        options={(props) => {
          const hasAccess = !hasNoAccess([
            'superadmin',
            'general_manager_marketing',
            'marketing_admin',
            'general',
          ]);
          const showGenerateSkpExternalButton = !hasNoAccess([
            'superadmin',
            'regional_manager',
            'regional_support',
            'key_account_manager',
            'group_key_account_manager',
            'national_key_account_manager',
            'general_manager_marketing',
            'general',
          ]);
          const route = props.route;
          let title = 'SKP Internal Detail';
          if (route?.params?.titleScreen) {
            title = route.params.titleScreen;
          }
          const removeSkpInternal = () => {
            dispatch(
              showAlert({
                show: true,
                title: 'Delete SKP Internal',
                description:
                  'This skp internal will be deleted. Do you want to proceed?',
                onCallback: async () => {
                  try {
                    if (route.params && route.params.id) {
                      dispatch(showPopup({ name: 'isLoading', value: true }));
                      const removeSkp = await MsOrder.deleteSkpInternal(
                        route.params.id,
                      );
                      if (removeSkp && removeSkp.data && removeSkp.data.data) {
                        dispatch(
                          showPopup({ name: 'isLoading', value: false }),
                        );
                        Toast.show({
                          text1: 'Successfully deleted SKP internal',
                          type: 'success',
                        });
                        props.navigation.navigate('SkpInternalScreen');
                      } else {
                        dispatch(
                          showPopup({ name: 'isLoading', value: false }),
                        );
                        Toast.show({
                          text1: 'Failed to delete SKP internal',
                          type: 'error',
                        });
                      }
                    }
                  } catch (error) {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'A system error occurred',
                      type: 'error',
                    });
                  }
                },
              }),
            );
          };

          const createSkpExternals = async () => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              const response = await MsOrder.generateSkpExternals(
                route.params.id,
              );
              if (response.data?.success) {
                dispatch(showPopup({ name: 'isLoading', value: false }));
                Toast.show({
                  text1: 'SKP Externals has been generated',
                  type: 'success',
                });
              } else {
                dispatch(showPopup({ name: 'isLoading', value: false }));
                Toast.show({
                  text1: 'Unable to generate SKP Externals',
                  type: 'error',
                });
              }
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message ||
                  'Unable to generate SKP Externals',
                type: 'error',
              });
            }
          };

          const menus = [];
          if (hasAccess && !['need_approval', 'archived'].includes(tempData)) {
            menus.push({
              type: 'redirect',
              name: 'Edit',
              screen: 'SkpInternalFormScreen',
              params: route.params,
            });
          }
          if (hasAccess && tempData == 'draft') {
            menus.push({
              type: 'function',
              name: 'Delete',
              callback: () => removeSkpInternal(),
            });
          }
          if (
            showGenerateSkpExternalButton &&
            tempData == 'approved' &&
            !skpGenerateStatus
          ) {
            menus.push({
              type: 'function',
              name: 'Generate SKP Externals',
              callback: () => createSkpExternals(),
            });
          }
          const goBack = () => {
            props.navigation.navigate('SkpInternalScreen');
          };
          return {
            title: setTitleHeader(title),
            headerLeft: () => (
              <BackButton
                {...props}
                notUseLastRoute={true}
                onPress={() => goBack()}
              />
            ),
            headerRight: () =>
              isWeb ? (
                <NotificationSettingButton {...props} />
              ) : menus?.length ? (
                <MoreButton menu={menus} {...props} />
              ) : (
                false
              ),
          };
        }}
      />
      <Stack.Screen
        name="SkpInternlBudgetTableScreen"
        component={SkpInternlBudgetTableScreen}
        options={(props) => {
          const route = props.route;
          const isDetailView = route.params.is_detail_view ? true : false;
          const title = 'Customer And Brand';
          const showAddBudget = () => {
            dispatch(showPopup({ name: 'showAddManual', value: true }));
          };
          const goBack = () => {
            if (isDetailView) {
              props.navigation.navigate(
                'SkpInternalDetailScreen',
                route.params,
              );
            } else {
              props.navigation.navigate('SkpInternalFormScreen', route.params);
            }
          };

          const recalculate = async () => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              if (props.route?.params?.id) {
                const response =
                  await MsOrder.calculateSkpInternalsBrandBudgetBulk(
                    props.route.params.id,
                  );
                if (response?.data?.success) {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1:
                      'Customer and Brand Budget are recalculating, Results will be sent to email',
                    type: 'success',
                  });
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Unable to recalculate',
                    type: 'error',
                  });
                }
              } else {
                dispatch(showPopup({ name: 'isLoading', value: false }));
                Toast.show({
                  text1: 'Unable to recalculate',
                  type: 'error',
                });
              }
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message || 'Unable to recalculate',
                type: 'error',
              });
            }
          };
          const menus = [
            { type: 'function', name: 'Add', callback: showAddBudget },
            { type: 'function', name: 'Recalculate', callback: recalculate },
          ];
          return {
            title: setTitleHeader(title),
            headerLeft: () => (
              <BackButton {...props} onPress={() => goBack()} />
            ),
            headerRight: isWeb
              ? () => <NotificationSettingButton {...props} />
              : !isDetailView
              ? () => <MoreButton menu={menus} {...props} />
              : false,
          };
        }}
      />
      <Stack.Screen
        name="SkpExternalListScreen"
        component={SkpExternalListScreen}
        options={(props) => {
          const route = props.route;
          const title = 'Promotion Agreement External';
          const goBack = () => {
            props.navigation.navigate('SkpInternalDetailScreen', route.params);
          };

          return {
            title: setTitleHeader(title),
            headerLeft: () => (
              <BackButton
                {...props}
                isHardBack={true}
                onPress={() => goBack()}
              />
            ),
            headerRight: isWeb
              ? () => <NotificationSettingButton {...props} />
              : false,
          };
        }}
      />
    </Stack.Navigator>
  );
};

export default SkpInternalNavigator;
